const WORD_MARKER_TEXT_PATTERN = /[\u200b-\u200f\u202a-\u202e\u2060\uFEFF\uFFF9-\uFFFB]/g;
const WORD_COMMENT_PATTERN = /<!--[\s\S]*?-->/g;
const WORD_XML_TAG_PATTERN = /<\/?(?:\w+:)?xml[^>]*>|<\/?o:p[^>]*>/gi;
const WORD_CONDITIONAL_PATTERN = /<!\[if[\s\S]*?<!\[endif\]>/gi;
const WORD_STYLE_BLOCK_PATTERN = /<style\b[^>]*>[\s\S]*?<\/style>/gi;
const WORD_META_LINK_PATTERN = /<(?:meta|link)\b[^>]*>/gi;

const removeWordOnlyStyle = (style = '') => style
  .split(';')
  .map((part) => part.trim())
  .filter(Boolean)
  .filter((declaration) => {
    const [property = '', rawValue = ''] = declaration.split(':');
    const normalizedProperty = property.trim().toLowerCase();
    const normalizedValue = rawValue.trim().toLowerCase();
    return normalizedProperty
      && !normalizedProperty.startsWith('mso-')
      && normalizedProperty !== 'tab-stops'
      && normalizedProperty !== 'language'
      && normalizedProperty !== 'text-underline'
      && !normalizedValue.includes('mso-')
      && !normalizedValue.includes('field-begin')
      && !normalizedValue.includes('field-end');
  })
  .join('; ');

const isWordMarkerElement = (element) => {
  const style = String(element.getAttribute('style') || '').toLowerCase();
  const className = String(element.getAttribute('class') || '').toLowerCase();
  const name = String(element.getAttribute('name') || '').toLowerCase();
  const id = String(element.getAttribute('id') || '').toLowerCase();
  return style.includes('mso-element:')
    || style.includes('field-begin')
    || style.includes('field-end')
    || className.includes('mso-element')
    || name.startsWith('_')
    || id.startsWith('_');
};

const removeEmptyWordInlineElements = (root) => {
  Array.from(root.querySelectorAll('span, a, font')).forEach((element) => {
    const hasMedia = element.querySelector('img, video, audio, iframe, source');
    const text = String(element.textContent || '').replace(WORD_MARKER_TEXT_PATTERN, '').replace(/\u00a0/g, ' ').trim();
    if (!hasMedia && !text && (isWordMarkerElement(element) || !element.attributes.length)) {
      element.remove();
    }
  });
};

const cleanWordDom = (html) => {
  const doc = document.implementation.createHTMLDocument('');
  doc.body.innerHTML = html;

  Array.from(doc.body.querySelectorAll('xml, o\\:p, style, meta, link')).forEach((element) => element.remove());
  Array.from(doc.body.querySelectorAll('*')).forEach((element) => {
    if (isWordMarkerElement(element)) {
      element.replaceWith(...Array.from(element.childNodes));
      return;
    }

    Array.from(element.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      if (
        name.startsWith('xmlns')
        || name.startsWith('on')
        || name === 'lang'
        || name === 'language'
        || name === 'face'
        || name === 'v:shapes'
        || name === 'data-contrast'
        || name === 'data-usefontface'
        || name === 'data-ccp'
      ) {
        element.removeAttribute(attribute.name);
      }
    });

    const className = String(element.getAttribute('class') || '')
      .split(/\s+/)
      .filter((part) => part && !/^mso/i.test(part))
      .join(' ');
    if (className) {
      element.setAttribute('class', className);
    } else {
      element.removeAttribute('class');
    }

    const nextStyle = removeWordOnlyStyle(element.getAttribute('style') || '');
    if (nextStyle) {
      element.setAttribute('style', nextStyle);
    } else {
      element.removeAttribute('style');
    }
  });

  removeEmptyWordInlineElements(doc.body);
  return doc.body.innerHTML;
};

export const cleanWordRichText = (value) => {
  const html = String(value || '')
    .replace(WORD_CONDITIONAL_PATTERN, '')
    .replace(WORD_COMMENT_PATTERN, '')
    .replace(WORD_XML_TAG_PATTERN, '')
    .replace(WORD_STYLE_BLOCK_PATTERN, '')
    .replace(WORD_META_LINK_PATTERN, '')
    .replace(WORD_MARKER_TEXT_PATTERN, '');

  if (!html.trim()) return '';

  if (typeof document === 'undefined' || !document.implementation?.createHTMLDocument) {
    return html
      .replace(/\s(?:class|lang|language|xmlns(?::\w+)?|data-(?:contrast|usefontface|ccp)|v:shapes)=(["']).*?\1/gi, '')
      .replace(/\sstyle=(["'])(?:(?!\1).)*mso-[^"']*\1/gi, '')
      .replace(/<span\b[^>]*(?:mso-element|field-begin|field-end)[^>]*><\/span>/gi, '');
  }

  return cleanWordDom(html).replace(WORD_MARKER_TEXT_PATTERN, '');
};

const BLOCK_BOUNDARY_PATTERN = /<\/?(?:p|div|br|li|tr|h[1-6]|blockquote)\b[^>]*>/gi;
const REMAINING_TAG_PATTERN = /<[^>]*>/g;
const SCRIPT_STYLE_PATTERN = /<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi;

/**
 * Converts stored rich-text (Quill/Word HTML) into readable plain text.
 * Descriptions are authored with a rich-text editor, so rendering the stored
 * value directly in JSX leaks the markup (`<p>Summer discounts</p>`).
 */
export const richTextToPlainText = (value) => {
  const html = String(value ?? '');
  if (!html.trim()) return '';

  const withoutEmbeds = html
    .replace(SCRIPT_STYLE_PATTERN, ' ')
    .replace(WORD_COMMENT_PATTERN, ' ');
  const spaced = withoutEmbeds
    .replace(BLOCK_BOUNDARY_PATTERN, ' ')
    .replace(REMAINING_TAG_PATTERN, '');

  const decoded =
    typeof document !== 'undefined' && document.implementation?.createHTMLDocument
      ? document.implementation.createHTMLDocument('').createElement('textarea')
      : null;

  let text = spaced;
  if (decoded) {
    decoded.innerHTML = spaced;
    text = decoded.value;
  }

  return text.replace(/ /g, ' ').replace(/\s+/g, ' ').trim();
};
